import { useEffect, useState } from "react";
import { PageHeader, EmptyState } from "../components/EmptyState";
import { api, ApiError, type CoachMessage } from "../lib/api";
import { fmtDateShort } from "../lib/format";

export default function Coach() {
  const [message, setMessage] = useState<CoachMessage | null | "error">(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.coachToday().then(setMessage).catch(() => setMessage("error"));
  }, []);

  const msg = message && message !== "error" ? message : null;
  const hasMessage = Boolean(msg?.content);

  async function generate() {
    setBusy(true);
    setErr(null);
    try {
      const fresh = await api.coachGenerate();
      setMessage(fresh);
    } catch (e) {
      if (e instanceof ApiError && e.status === 503) {
        setErr("Daily coaching isn't set up yet (no Anthropic key on the server).");
      } else {
        setErr(e instanceof ApiError ? `API ${e.status}` : (e as Error).message);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl animate-fade-in">
      <PageHeader
        title="Coach"
        eyebrow="Your daily message"
        action={
          hasMessage ? (
            <button type="button" className="btn-secondary" onClick={generate} disabled={busy}>
              {busy ? "Thinking…" : "Re-roll"}
            </button>
          ) : null
        }
      />

      {hasMessage ? (
        <div className="card-pad">
          <p className="whitespace-pre-wrap font-serif text-[15px] leading-relaxed">{msg!.content}</p>
          <div className="mt-6 flex items-center justify-between border-t border-stone-200 pt-4">
            <span className="muted text-sm">{fmtDateShort(msg!.generated_at)}</span>
            {msg!.model ? <span className="pill-neutral">{msg!.model}</span> : null}
          </div>
        </div>
      ) : (
        <EmptyState
          title="No coaching message yet"
          action={
            <button type="button" className="btn-accent" onClick={generate} disabled={busy}>
              {busy ? "Thinking…" : "Generate now"}
            </button>
          }
        >
          A personalised morning message appears here each day. It reads your real weight trend,
          sleep, calories and goals — no generic motivation. It auto-generates at your set hour,
          or tap Generate to create one now.
        </EmptyState>
      )}
      {err && <div className="mt-4 text-sm text-loss">{err}</div>}
    </div>
  );
}
